import { Component, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subscription, filter } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { AppState } from 'src/app/store/app.reducer';
import * as AuthActions from 'src/app/store/auth/auth.actions';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import * as UserActions from 'src/app/store/user/user.actions';
import { selectShowProgressSpinner } from 'src/app/store/notifications/notifications.selector';
import { StandardDesignParameters, StandardPageLayout } from '../../stdlayoutlib/classes.stdlayoutlib';
import { selectCurrentLang, selectRoleExplainedTechnician, selectUser } from 'src/app/store/user/user.selector';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { User } from 'src/app/model/user';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Component({ // user signup component
	selector: 'app-close-account',
	templateUrl: './close-account.component.html',
	providers: [ConfirmationService]
})
export class ClouseAccountComponent implements OnInit, OnDestroy {
	// Form
	closeAccountForm: FormGroup;
	confirm = false;
	showPassword = false;
	accountClosedSub: Subscription;

	iAmTechnician: boolean;

	typeUserSel$: Observable<boolean | undefined>;
	typeUserSub: Subscription | undefined;

	// Languages & Dialog Language
	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	// Design
	screenWidth: number;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	showProgressSpinner: boolean;
	progressSpinnerSel$: Observable<boolean>;
	progressSpinnerSub: Subscription | undefined;

	constructor(
		public renderer: Renderer2,
		public app: AppComponent,
		private route: ActivatedRoute,
		private store: Store<AppState>,
		private router: Router,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();

		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventCustomPageView({ params: { page_title: 'Close account page' } }));
		}, 2000);
	}

	buildForm(): void {
		this.closeAccountForm = new FormGroup({
			'closeFlag': new FormControl(false, Validators.requiredTrue), /*ID = 4 */
			'password': new FormControl({ value: '', disabled: true }, Validators.required),
		});
		this.accountClosedSub = this.store.select('user', 'accountClosed')
			.pipe(filter(value => !!value)).subscribe(() => {
				this.store.dispatch(AuthActions.authLogout());
			});
	}

	ngOnDestroy() {
		this.accountClosedSub?.unsubscribe();
		this.progressSpinnerSub?.unsubscribe();
		this.typeUserSub?.unsubscribe();
	}

	initSelectors(): void {
		// Store selectors
		this.progressSpinnerSel$ = this.store.select(selectShowProgressSpinner);
		this.typeUserSel$ = this.store.select(selectRoleExplainedTechnician);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.currentUser$ = this.store.select(selectUser);
	}

	setConfirmCheckbox(): void {
		if (this.f.closeFlag.value) {
			this.f.password.enable();
		} else {
			this.f.password.disable();
		}
	}

	get f(): any {
		return this.closeAccountForm.controls;
	}

	subscribeAll(): void {
		this.progressSpinnerSub = this.progressSpinnerSel$.subscribe(
			(showProgressSpinner: boolean) => {
				this.showProgressSpinner = showProgressSpinner;
			}
		);
		this.typeUserSub = this.typeUserSel$.subscribe(
			(isTechnician: boolean) => {
				this.iAmTechnician = isTechnician;
			}
		);
		this.currentLang$.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
	}

	closeAccount(event: Event): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			target: event.target as EventTarget,
			header: this.translate.instant('CLOSE_ACCOUNT_CONFIRM_DIALOG_TITLE'),
			message: this.translate.instant('CLOSE_ACCOUNT_CONFIRM_DIALOG_BODY'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			rejectButtonStyleClass: "p-button-text",
			accept: () => {
				this.store.dispatch(NotificationsActions.showProgressSpinner());
				this.store.dispatch(UserActions.closeAccount({
					pwd: this.f.password.value,
					countryName: this.currentUser.CountryName,
					iAmTechnician: this.iAmTechnician, lang: this.currentLang
				}));
			},
			reject: () => {
			}
		});
	}

	backToHome(): void {
		this.router.navigate(['../profile'], { relativeTo: this.route });
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicBorderLabelMobile(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return (StandardPageLayout.onExtendedMobile(this.screenWidth)) ? "w-full h-2_5rem" : "w-13_5rem h-2rem";
	}

	justifyContentStyleDesktop(): string {
		return (this.screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) ? "justify-content-center" : "justify-content-start";
	}

	justifyTextStyleDesktop(): string {
		return (this.screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) ? "text-center" : "text-start";
	}
}
